<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref } from 'vue';

defineProps<{ storm?: boolean }>();

const mouseX = ref(0.5);
const mouseY = ref(0.3);

// rAF 节流：原始 ~60Hz mousemove 全部进 reactive 会让 5 个 blob 每帧重算 inline style；
// blob 自身 transform 已有 1.2s CSS transition，丢掉中间帧视觉无差别但 reactive churn 减 ~6x。
let pending: { x: number; y: number } | null = null;
let raf = 0;

function onMove(e: MouseEvent) {
  pending = { x: e.clientX / window.innerWidth, y: e.clientY / window.innerHeight };
  if (raf) return;
  raf = requestAnimationFrame(() => {
    raf = 0;
    if (!pending) return;
    mouseX.value = pending.x;
    mouseY.value = pending.y;
    pending = null;
  });
}

onMounted(() => window.addEventListener('mousemove', onMove, { passive: true }));
onBeforeUnmount(() => {
  window.removeEventListener('mousemove', onMove);
  if (raf) cancelAnimationFrame(raf);
});

const blobs = [
  { x: 5, y: 8, size: 520, color: 'var(--aurora-accent)', opacity: 0.55, anim: 'auroraDrift0' },
  { x: 55, y: 18, size: 600, color: 'var(--aurora-magenta)', opacity: 0.48, anim: 'auroraDrift1' },
  {
    x: 0,
    y: 55,
    size: 500,
    color: 'var(--aurora-blob-violet)',
    opacity: 0.6,
    anim: 'auroraDrift2',
  },
  {
    x: 50,
    y: 65,
    size: 560,
    color: 'var(--aurora-accent-soft)',
    opacity: 0.35,
    anim: 'auroraDrift3',
  },
  {
    x: 25,
    y: 40,
    size: 300,
    color: 'var(--aurora-blob-gold)',
    opacity: 0.28,
    anim: 'auroraDrift4',
  },
];
</script>

<template>
  <div aria-hidden class="aurora-bg" :class="{ 'aurora-bg--storm': storm }">
    <div
      v-for="(b, i) in blobs"
      :key="i"
      class="aurora-blob"
      :style="{
        top: `${b.y}%`,
        left: `${b.x}%`,
        width: `${b.size}px`,
        height: `${b.size}px`,
        background: b.color,
        opacity: storm ? Math.min(1, b.opacity * 2) : b.opacity,
        transform: `translate(${(mouseX - 0.5) * (i % 2 ? 30 : -30)}px, ${(mouseY - 0.5) * (i % 2 ? -20 : 20)}px)`,
        animation: `${b.anim} ${(storm ? 0.35 : 1) * [22, 18, 26, 20, 16][i]}s ease-in-out infinite`,
      }"
    />
  </div>
</template>

<style scoped>
.aurora-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  background: var(--aurora-bg);
  pointer-events: none;
}
.aurora-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  mix-blend-mode: screen;
  transition:
    transform 1.2s cubic-bezier(0.2, 0.7, 0.3, 1),
    opacity 0.6s ease,
    filter 0.6s ease;
}
/* Konami code 风暴：blur 缩小 + saturation 提升 → 颜色更脏更艳 */
.aurora-bg--storm .aurora-blob {
  filter: blur(50px) saturate(1.6);
}
@keyframes auroraDrift0 {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  50% {
    transform: translate(40px, -30px) scale(1.08);
  }
}
@keyframes auroraDrift1 {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  50% {
    transform: translate(-50px, 30px) scale(0.92);
  }
}
@keyframes auroraDrift2 {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  50% {
    transform: translate(30px, 50px) scale(1.12);
  }
}
@keyframes auroraDrift3 {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  50% {
    transform: translate(-30px, -40px) scale(0.95);
  }
}
@keyframes auroraDrift4 {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  50% {
    transform: translate(25px, -35px) scale(1.1);
  }
}
</style>
